import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Teams from './pages/Teams';
import Personnel from './pages/Personnel';
import Roster from './pages/Roster';
import Sidebar from './components/common/Sidebar';
import TopBar from './components/common/TopBar';
import { connectSocket, disconnectSocket } from './services/socket';

function AppLayout({ children, user, alerts, onLogout }) {
  const location = useLocation();
  const pendingCount = alerts.filter(a => a.status === 'pending').length;
  return (
    <div className="app-layout">
      <Sidebar user={user} currentPath={location.pathname} pendingAlerts={pendingCount} onLogout={onLogout} />
      <div className="main-content">
        <TopBar user={user} activeAlerts={alerts.filter(a => !['resolved','cancelled'].includes(a.status)).length} />
        {children}
      </div>
    </div>
  );
}

function ProtectedRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Looping alert siren until acknowledged
function useAlertSiren() {
  const oscRef = useRef(null);
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);

  const start = useCallback(() => {
    if (intervalRef.current) return; // already playing
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const playBeep = () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'square';
        gain.gain.value = 0.08;
        osc.frequency.value = 880;
        osc.start();
        setTimeout(() => { try { osc.frequency.value = 660; } catch(e){} }, 200);
        setTimeout(() => { try { osc.stop(); } catch(e){} }, 500);
      };
      playBeep();
      intervalRef.current = setInterval(playBeep, 1200);
    } catch (e) { console.warn('Audio failed:', e); }
  }, []);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (ctxRef.current) {
      try { ctxRef.current.close(); } catch(e){}
      ctxRef.current = null;
    }
  }, []);

  useEffect(() => () => stop(), [stop]);
  return { start, stop };
}

export default function App() {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('ops_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [alerts, setAlerts] = useState([]);
  const [liveLocations, setLiveLocations] = useState({}); // alertId -> {lat,lng,updatedAt}
  const siren = useAlertSiren();

  // Browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Auto-stop siren when no pending alerts
  useEffect(() => {
    const hasPending = alerts.some(a => a.status === 'pending');
    if (!hasPending) siren.stop();
  }, [alerts, siren]);

  const showBrowserNotification = useCallback((alert) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      const notif = new Notification(`🆘 ${alert.category_icon} ${alert.category_name} ALERT`, {
        body: `${alert.user_name || 'Unknown'} • ${alert.priority || 'HIGH'} priority`,
        tag: alert.id,
        requireInteraction: true,
        icon: '/favicon.ico'
      });
      notif.onclick = () => { window.focus(); notif.close(); };
    } catch (e) { console.warn('Notification failed:', e); }
  }, []);

  const setupSocketHandlers = useCallback((socket) => {
    socket.on('alert:new', (alert) => {
      console.log('🆘 NEW ALERT:', alert);
      setAlerts(prev => {
        if (prev.find(a => a.id === alert.id)) return prev;
        return [alert, ...prev];
      });
      siren.start();
      showBrowserNotification(alert);
    });

    socket.on('alert:update', (update) => {
      setAlerts(prev => prev.map(a => a.id === update.id ? { ...a, ...update } : a));
    });

    socket.on('location:update', (data) => {
      console.log('📍 Location update:', data);
      setLiveLocations(prev => ({
        ...prev,
        [data.alert_id || data.alertId]: {
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: data.accuracy,
          speed: data.speed,
          updatedAt: Date.now()
        }
      }));
      // Also update the alert's main lat/lng so the marker moves
      setAlerts(prev => prev.map(a =>
        a.id === (data.alert_id || data.alertId)
          ? { ...a, latitude: data.latitude, longitude: data.longitude }
          : a
      ));
    });
  }, [siren, showBrowserNotification]);

  const handleLogin = useCallback((userData, token) => {
    localStorage.setItem('ops_token', token);
    localStorage.setItem('ops_user', JSON.stringify(userData));
    setUser(userData);
    const socket = connectSocket(token);
    setupSocketHandlers(socket);
  }, [setupSocketHandlers]);

  const handleLogout = useCallback(() => {
    siren.stop();
    disconnectSocket();
    localStorage.removeItem('ops_token');
    localStorage.removeItem('ops_user');
    setUser(null);
    setAlerts([]);
    setLiveLocations({});
  }, [siren]);

  // Reconnect on page reload if token exists
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('ops_token');
      if (token) {
        const socket = connectSocket(token);
        setupSocketHandlers(socket);
      }
    }
    return () => disconnectSocket();
  }, []); // run once on mount

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={
          user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
        } />
        <Route path="/" element={
          <ProtectedRoute user={user}>
            <AppLayout user={user} alerts={alerts} onLogout={handleLogout}>
              <Dashboard
                alerts={alerts}
                setAlerts={setAlerts}
                liveLocations={liveLocations}
                onAcknowledge={() => siren.stop()}
              />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/teams" element={
          <ProtectedRoute user={user}>
            <AppLayout user={user} alerts={alerts} onLogout={handleLogout}>
              <Teams />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/personnel" element={
          <ProtectedRoute user={user}>
            <AppLayout user={user} alerts={alerts} onLogout={handleLogout}>
              <Personnel />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/roster" element={
          <ProtectedRoute user={user}>
            <AppLayout user={user} alerts={alerts} onLogout={handleLogout}>
              <Roster />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
